import {
  CAccordion,
  CAccordionBody,
  CAccordionHeader,
  CAccordionItem,
  CCloseButton,
  CCol,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CRow,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import {
  EnvelopeIcon,
  MapPinIcon,
  PhoneIcon,
  UserCircleIcon,
} from "@heroicons/react/24/solid";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { Link, useSearchParams } from "react-router-dom";
import { HeaderSort } from "../../components/TableHeaderSort";
import { BookingStatus, BookingTypes } from "../../helpers/enums";
import {
  asActivity,
  formatDateString,
  getActivePrice,
  getActivityView,
  getBadgeClassNameByBookingStatus,
  getBookingHeader,
  getBookingStatus,
  getBookingStatusDisplayValue,
  getPaymentMethodLabel,
  isActivityBooking,
} from "../../helpers/general";
import { useAuth } from "../../hooks/useAuth";
import { useCookie } from "../../hooks/useCookie";
import { useQueryParam } from "../../hooks/useQueryParam";
import { useToast } from "../../hooks/useToast";
import { general } from "../../locales/general";
import apiService from "../../service/apiService";

interface TableBodyProps {
  booking: Booking;
  showDetails: (booking: Booking) => void;
  mode: "actions" | "list";
  showItemColumn: boolean;
}

/* -------------------------------------------------------------------------- */
/*  Table row                                                                 */
/* -------------------------------------------------------------------------- */

function TableBodyRowContent({
  booking,
  showDetails,
  mode,
  showItemColumn,
}: TableBodyProps) {
  const { token } = useCookie("vToken");
  const queryClient = useQueryClient();

  const { toastSuccess, toastError } = useToast();

  const AcceptBooking = useMutation((id: string) => {
    return apiService.MakePutRequest(`booking/${id}/approve`, {}, token);
  });

  const AcceptBookingButton = () => {
    AcceptBooking.mutate(booking.id, {
      onError(error) {
        toastError(general.fr.message.operationFailed);
      },
      onSuccess() {
        toastSuccess("Réservation acceptée");
        queryClient.invalidateQueries(["getAllBookings"]);
      },
    });
  };

  const RefuseBooking = useMutation((id: string) => {
    return apiService.MakePutRequest(`booking/${id}/refuse`, {}, token);
  });

  const RefuseBookingButton = () => {
    RefuseBooking.mutate(booking.id, {
      onError(error) {
        toastError(general.fr.message.operationFailed);
      },
      onSuccess() {
        toastSuccess("Réservation refusée");
        queryClient.invalidateQueries(["getAllBookings"]);
      },
    });
  };

  const { isAdmin, isPlaceAdmin } = useAuth();
  const header = getBookingHeader(booking);

  return (
    <CTableRow
      style={{
        width: "100%",
        position: "relative",
      }}
    >
      {showItemColumn && (
        <CTableDataCell className="px-3">
          <div className="d-flex align-items-center gap-3">
            <img
              src={
                header.image ??
                `https://ui-avatars.com/api/?name=${encodeURIComponent(
                  header.title ?? "",
                )}`
              }
              alt=""
              width={30}
              height={30}
              style={{ borderRadius: "100%", objectFit: "cover" }}
            />

            <div>
              <p className="fw-bold m-0 text-capitalize text-sm">
                {header.title}
              </p>
              <p className="m-0 text-sm">{header.subtitle}</p>
            </div>
          </div>
        </CTableDataCell>
      )}
      <CTableDataCell className="p-3 text-capitalize text-sm">
        {formatDateString(booking?.date!)} - {booking?.hour}
      </CTableDataCell>

      {isAdmin && mode === "actions" ? (
        <CTableDataCell className="p-3 text-sm">
          <Link
            to={`/users/${booking?.user?.id}`}
            className="fw-bold m-0 text-capitalize"
          >
            {booking.user?.fullName}
          </Link>
        </CTableDataCell>
      ) : (
        mode === "actions" && (
          <CTableDataCell className="p-3 text-sm">
            <p className=" m-0 text-capitalize">{booking.user?.fullName}</p>
          </CTableDataCell>
        )
      )}

      <CTableDataCell className="p-3 text-sm">
        {booking?.reference}
      </CTableDataCell>
      {isPlaceAdmin && (
        <>
          <CTableDataCell className="p-3 text-capitalize text-sm">
            {booking?.scanedBy ? booking?.scanedBy.fullName : "-"}
          </CTableDataCell>
          <CTableDataCell className="p-3 text-capitalize text-sm">
            {booking?.scanedAt
              ? new Date(booking?.scanedAt).toDateString()
              : "-"}
          </CTableDataCell>
        </>
      )}
      <CTableDataCell className={` p-3 text-capitalize text-sm`}>
        <div className={`${getBadgeClassNameByBookingStatus(booking)}`}>
          {getBookingStatusDisplayValue(booking)}
        </div>
      </CTableDataCell>
      <CTableDataCell className="p-3 text-capitalize text-sm">
        {getPaymentMethodLabel(booking)}
      </CTableDataCell>
      {isAdmin ? (
        <CTableDataCell className="p-3 text-sm">
          {booking?.bookingSource}
        </CTableDataCell>
      ) : null}
      <CTableDataCell className="p-3 text-sm">
        <div className="p-3 text-capitalize text-sm d-flex align-items-center gap-3">
          {getBookingStatus(booking) === BookingStatus.WAITING && (
            <>
              <button
                className="btn btn-primary shadow-secondary rounded-pill text-sm text-white"
                onClick={AcceptBookingButton}
                disabled={AcceptBooking.isLoading}
              >
                {AcceptBooking.isLoading ? <CSpinner size="sm" /> : " Accepter"}
              </button>
              <button
                className="btn btn-danger shadow-primary rounded-pill text-sm text-white"
                onClick={RefuseBookingButton}
                disabled={RefuseBooking.isLoading}
              >
                {RefuseBooking.isLoading ? <CSpinner size="sm" /> : " Refuser"}
              </button>
            </>
          )}
          {getBookingStatus(booking) === BookingStatus.CONFIRMED && (
            <button
              className="btn btn-danger shadow-primary rounded-pill text-sm text-white"
              onClick={RefuseBookingButton}
              disabled={RefuseBooking.isLoading}
            >
              {RefuseBooking.isLoading ? <CSpinner size="sm" /> : " Refuser"}
            </button>
          )}
          <button
            onClick={() => showDetails(booking)}
            className="btn rounded-pill border py-1 px-3 text-sm"
          >
            Vue
          </button>
        </div>
      </CTableDataCell>
    </CTableRow>
  );
}

/* -------------------------------------------------------------------------- */
/*  Activity details (offcanvas)                                              */
/* -------------------------------------------------------------------------- */

function ActivityBookingDetails({ booking }: { booking: Booking }) {
  const view = getActivityView(booking);
  const { pickupLocation, pickupLocationUnknown } = asActivity(booking);

  const isEuro = booking.priceToPayCurrency === "euro";
  const currency = isEuro ? "€" : "DH";

  const lineTotal = (ob: (typeof view.optionBookings)[number]) => {
    const unit = isEuro
      ? Number(ob.ageGroupSnapshot?.priceInEuro ?? ob.unitPrice ?? 0)
      : Number(ob.unitPrice ?? ob.ageGroupSnapshot?.price ?? 0);
    return unit * ob.numberOfReservation;
  };

  const subtotal = view.optionBookings.reduce((t, ob) => t + lineTotal(ob), 0);
  const promo = booking?.appliedPromoCodes?.[0];
  const hasPromo = (booking?.appliedPromoCodes?.length ?? 0) > 0;

  const hasCoordinates =
    pickupLocation?.latitude != null && pickupLocation?.longitude != null;

  return (
    <div className="mt-4 bg-primary-shade p-2 rounded-sm">
      <p className="mb-2 border-bottom fw-bold">Activité réservée</p>

      <div className="d-flex align-items-center gap-2 mb-3">
        {view.image && (
          <img
            src={view.image}
            alt=""
            width={48}
            height={48}
            className="rounded-sm"
            style={{ objectFit: "cover" }}
          />
        )}
        <div>
          <p className="mb-0 fw-bold">{view.title ?? "Activité"}</p>
          {view.options.length > 0 && (
            <p className="mb-0 text-sm">Option : {view.options.join(", ")}</p>
          )}
        </div>
      </div>

      {view.optionBookings.map((ob) => (
        <CRow
          key={ob.id}
          className="mb-2 align-items-center justify-content-between"
        >
          <CCol sm="auto">
            {ob.numberOfReservation} x {ob.ageGroupSnapshot?.label}
          </CCol>
          <CCol sm="auto">
            {lineTotal(ob)} {currency}
          </CCol>
        </CRow>
      ))}

      {(pickupLocation || pickupLocationUnknown) && (
        <div className="mt-3 text-sm">
          <MapPinIcon width={15} />{" "}
          {pickupLocationUnknown ? (
            "Lieu de prise en charge non précisé"
          ) : pickupLocation?.address ? (
            <>Prise en charge : {pickupLocation.address}</>
          ) : hasCoordinates ? (
            <a
              href={`https://www.google.com/maps?q=${pickupLocation?.latitude},${pickupLocation?.longitude}`}
              target="_blank"
              rel="noreferrer"
            >
              Voir le lieu de prise en charge
            </a>
          ) : null}
        </div>
      )}

      {hasPromo && (
        <>
          <CRow className="mt-3 pt-2 mb-2 align-items-center justify-content-between border-top">
            <CCol sm="auto">
              <strong style={{ fontSize: "14px" }}>Sous-Total:</strong>
            </CCol>
            <CCol sm="auto">
              {subtotal} {currency}
            </CCol>
          </CRow>
          <CRow className="mb-2 align-items-center justify-content-between">
            <CCol sm="auto">
              <strong style={{ fontSize: "14px" }}>
                Remise ({promo?.discount}%):
              </strong>
            </CCol>
            <CCol sm="auto">
              - ({subtotal - Number(booking?.priceToPay ?? 0)} {currency})
            </CCol>
          </CRow>
        </>
      )}

      <CRow className="mb-2 align-items-center justify-content-between border-top pt-2 mt-2">
        <CCol sm="auto">
          <strong style={{ fontSize: "14px" }}>Total:</strong>
        </CCol>
        <CCol sm="auto">
          <strong>
            {booking?.priceToPay} {currency}
          </strong>
        </CCol>
      </CRow>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Table                                                                     */
/* -------------------------------------------------------------------------- */

interface TableProps {
  bookings: Booking[];
  mode?: "actions" | "list";
}
export default function BookingTable({
  bookings,
  mode = "actions",
}: TableProps) {
  const [searchParams] = useSearchParams();
  const { add } = useQueryParam();
  const { isAdmin, isPlaceAdmin } = useAuth();
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [sort, setSort] = useState({
    field: "createdAt",
    order: "desc",
  });

  // Admins always see the "Établissement" column. Place admins only get a
  // "Réservation" column when the list contains activity bookings, because
  // that is the only way for them to see which activity / option was booked.
  const hasActivityBookings = bookings?.some((b) => isActivityBooking(b));
  const showItemColumn = Boolean(isAdmin) || Boolean(hasActivityBookings);

  const resetSort = () => setSort({ field: "createdAt", order: "desc" });

  useEffect(() => {
    if (searchParams.get("sort")) {
      const [field, order] = searchParams.get("sort")!.split(":");
      setSort({ field, order });
    } else {
      resetSort();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    add("sort", `${sort.field}:${sort.order}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort]);

  return (
    <div className="mt-3">
      <CTable responsive>
        <CTableHead>
          <CTableRow>
            {showItemColumn && (
              <CTableHeaderCell scope="col" className="text-sm">
                <div className="d-flex align-items-center justify-content-between">
                  {isAdmin ? "Établissement" : "Réservation"}{" "}
                  <div style={{ height: "40px", width: "20px" }}></div>
                </div>
              </CTableHeaderCell>
            )}

            <CTableHeaderCell scope="col" className="text-sm">
              <div className="d-flex align-items-center justify-content-between">
                Date/Heure de réservation
                {mode === "actions" ? (
                  <HeaderSort
                    field="date"
                    sort={sort.order}
                    sortedField={sort}
                    setSort={(field, order) => setSort({ field, order })}
                    clearSort={() => resetSort()}
                    defaultField="createdAt"
                  />
                ) : (
                  <div style={{ height: "40px", width: "20px" }}></div>
                )}
              </div>
            </CTableHeaderCell>
            {mode === "actions" ? (
              <CTableHeaderCell scope="col" className="text-sm">
                <div className="d-flex align-items-center justify-content-between">
                  Utilisateur{" "}
                  <div style={{ height: "40px", width: "20px" }}></div>
                </div>
              </CTableHeaderCell>
            ) : null}
            <CTableHeaderCell scope="col" className="text-sm">
              <div className="d-flex align-items-center justify-content-between">
                Référence
                {mode === "actions" ? (
                  <HeaderSort
                    field="reference"
                    sort={sort.order}
                    sortedField={sort}
                    setSort={(field, order) => setSort({ field, order })}
                    clearSort={() => resetSort()}
                    defaultField="createdAt"
                  />
                ) : (
                  <div style={{ height: "40px", width: "20px" }}></div>
                )}
              </div>
            </CTableHeaderCell>

            {isPlaceAdmin && (
              <>
                <CTableHeaderCell scope="col" className="text-sm">
                  <div className="d-flex align-items-center justify-content-between">
                    Scanné par{" "}
                    <div style={{ height: "40px", width: "20px" }}></div>
                  </div>
                </CTableHeaderCell>
                <CTableHeaderCell scope="col" className="text-sm">
                  <div className="d-flex align-items-center justify-content-between">
                    Scanné à{" "}
                    <div style={{ height: "40px", width: "20px" }}></div>
                  </div>
                </CTableHeaderCell>
              </>
            )}
            <CTableHeaderCell scope="col" className="text-sm">
              <div className="d-flex align-items-center justify-content-between">
                Statut <div style={{ height: "40px", width: "20px" }}></div>
              </div>
            </CTableHeaderCell>

            <CTableHeaderCell scope="col" className="text-sm">
              <div className="d-flex align-items-center justify-content-between">
                Méthode de payement{" "}
                {mode === "actions" ? (
                  <HeaderSort
                    field="paidInApp"
                    sort={sort.order}
                    sortedField={sort}
                    setSort={(field, order) => setSort({ field, order })}
                    clearSort={() => resetSort()}
                    defaultField="createdAt"
                  />
                ) : (
                  <div style={{ height: "40px", width: "20px" }}></div>
                )}
              </div>
            </CTableHeaderCell>

            {isAdmin === true ? (
              <CTableHeaderCell scope="col" className="text-sm">
                <div className="d-flex align-items-center justify-content-between">
                  Source de réservation{" "}
                  {mode === "actions" ? (
                    <HeaderSort
                      field="bookingSource"
                      sort={sort.order}
                      sortedField={sort}
                      setSort={(field, order) => setSort({ field, order })}
                      clearSort={() => resetSort()}
                      defaultField="createdAt"
                    />
                  ) : (
                    <div style={{ height: "40px", width: "20px" }}></div>
                  )}
                </div>
              </CTableHeaderCell>
            ) : null}

            <CTableHeaderCell
              scope="col"
              className="text-sm"
            ></CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {bookings?.map((booking) => (
            <TableBodyRowContent
              key={booking.id}
              booking={booking}
              showDetails={(booking: Booking) => setSelectedBooking(booking)}
              mode={mode}
              showItemColumn={showItemColumn}
            />
          ))}
        </CTableBody>
      </CTable>
      <COffcanvas
        backdrop={true}
        placement="start"
        visible={Object.keys(selectedBooking ?? {}).length > 0}
        onHide={() => setSelectedBooking(null)}
      >
        <div className="border-bottom pb-1 px-3">
          <COffcanvasHeader className="mb-0">
            <COffcanvasTitle>Détails des réservations</COffcanvasTitle>
            <CCloseButton
              className="text-reset"
              onClick={() => setSelectedBooking(null)}
            />
          </COffcanvasHeader>
          <div className="d-flex align-items-center justify-content-between">
            <div className="mb-1">
              <UserCircleIcon width={20} />{" "}
              <span className="text-sm">{selectedBooking?.user.fullName}</span>
            </div>
            <div className="mb-1">
              <PhoneIcon width={15} />{" "}
              <span className="text-sm">{selectedBooking?.user.phone}</span>
            </div>
          </div>
          <div className="mb-1">
            <EnvelopeIcon width={15} />{" "}
            <span className="text-sm">{selectedBooking?.user.email}</span>
          </div>
        </div>
        <COffcanvasBody>
          <CRow className="align-items-center justify-content-between">
            <CCol sm="auto">{getBookingHeader(selectedBooking).title}</CCol>
            <CCol sm="auto">
              <button className="btn btn-danger rounded-full py-0 px-2 text-white">
                {selectedBooking &&
                  getBookingStatusDisplayValue(selectedBooking)}
              </button>
            </CCol>
          </CRow>

          <CRow className="align-items-center justify-content-between mt-3">
            <CCol sm="auto" title="Reference">
              <p className="text-sm mb-0">Reference</p>
              {selectedBooking?.reference}
            </CCol>
          </CRow>

          {selectedBooking?.type === BookingTypes.PLACE_BOOKING ||
          (selectedBooking?.type === BookingTypes.SPECIAL_OFFER_BOOKING &&
            selectedBooking?.variantsBookings!?.length === 0) ? (
            <CRow className="bg-primary-shade p-2 rounded-sm align-items-center justify-content-between mt-3">
              <CCol sm="auto" title="Reference">
                Nombre de personnes: {selectedBooking?.personsNumber}
              </CCol>
            </CRow>
          ) : null}

          <CRow className="align-items-center justify-content-between mt-3">
            <CCol sm="auto" title="Reference">
              Méthode de payement: {getPaymentMethodLabel(selectedBooking)}
            </CCol>
          </CRow>

          {selectedBooking && isActivityBooking(selectedBooking) && (
            <ActivityBookingDetails booking={selectedBooking} />
          )}

          {selectedBooking?.packsBookings!?.length > 0 && (
            <div className="mt-4">
              <p className="mb-2 border-bottom fw-bold">Pack réservé</p>
              <CAccordion flush>
                {selectedBooking?.packsBookings.map((pack) => (
                  <CAccordionItem key={pack.id}>
                    <CAccordionHeader className="align-items-center justify-content-between w-100">
                      <div className="d-flex align-items-center gap-2 w-75">
                        <img
                          src={pack.pack.image}
                          alt=""
                          width={40}
                          height={40}
                          className="rounded-sm"
                        />

                        <p className="mb-0">
                          {pack.numberOfReservation} x {pack.pack.title}{" "}
                        </p>
                      </div>
                      {selectedBooking.priceToPayCurrency === "euro" ? (
                        <p className="mb-0">
                          {/* @ts-ignore */}
                          {pack.pack.priceInEuro * pack.numberOfReservation}€
                        </p>
                      ) : (
                        <p className="mb-0">
                          {/* @ts-ignore */}
                          {pack.pack.price * pack.numberOfReservation}
                          DH
                        </p>
                      )}
                    </CAccordionHeader>
                    <CAccordionBody>
                      <ul>
                        {pack.pack.items.map((packItem) => (
                          <li key={packItem.id} className="mb-1">
                            {packItem.title}
                          </li>
                        ))}
                      </ul>
                    </CAccordionBody>
                  </CAccordionItem>
                ))}
              </CAccordion>
            </div>
          )}

          {selectedBooking?.variantsBookings &&
          selectedBooking?.variantsBookings.length > 0 ? (
            // ==========================================
            // BRANCH 1: OFFER WITH VARIANTS
            // ==========================================
            <div className="mt-4 bg-primary-shade p-2 rounded-sm">
              <p className="mb-2 border-bottom fw-bold">
                Variant(s) réservé(s)
              </p>
              {selectedBooking?.variantsBookings.map((item) => (
                <CRow
                  key={item.id}
                  className="mb-2 align-items-center justify-content-between"
                >
                  <CCol sm="auto">
                    {item.numberOfReservation} x {item.variant.description}
                  </CCol>
                  {selectedBooking.priceToPayCurrency === "euro" ? (
                    <CCol sm="auto">
                      {item.numberOfReservation *
                        getActivePrice(
                          selectedBooking,
                          item.variant.priceInEuro,
                          item.variant.nightPriceInEuro,
                        )}{" "}
                      €
                    </CCol>
                  ) : (
                    <CCol sm="auto">
                      {item.numberOfReservation *
                        getActivePrice(
                          selectedBooking,
                          item.variant.price,
                          item.variant.nightPrice,
                        )}{" "}
                      DH
                    </CCol>
                  )}
                </CRow>
              ))}

              {selectedBooking?.appliedPromoCodes &&
                selectedBooking?.appliedPromoCodes?.length > 0 && (
                  <>
                    <CRow className=" mt-3 pt-2 mb-2 align-items-center justify-content-between border-top">
                      <CCol sm="auto">
                        <strong style={{ fontSize: "14px" }}>
                          Sous-Total:
                        </strong>
                      </CCol>
                      <CCol sm="auto">
                        {selectedBooking.priceToPayCurrency === "euro"
                          ? selectedBooking?.variantsBookings?.reduce(
                              (total, item) =>
                                total +
                                getActivePrice(
                                  selectedBooking,
                                  item.variant.priceInEuro,
                                  item.variant.nightPriceInEuro,
                                ) *
                                  item.numberOfReservation,
                              0,
                            )
                          : selectedBooking?.variantsBookings?.reduce(
                              (total, item) =>
                                total +
                                getActivePrice(
                                  selectedBooking,
                                  item.variant.price,
                                  item.variant.nightPrice,
                                ) *
                                  item.numberOfReservation,
                              0,
                            )}{" "}
                        {selectedBooking.priceToPayCurrency === "euro"
                          ? "€"
                          : "DH"}
                      </CCol>
                    </CRow>
                    <CRow className="mb-2 align-items-center justify-content-between">
                      <CCol sm="auto">
                        <strong style={{ fontSize: "14px" }}>
                          Remise (
                          {selectedBooking?.appliedPromoCodes[0].discount}%):
                        </strong>
                      </CCol>
                      <CCol sm="auto">
                        - (
                        {(selectedBooking.priceToPayCurrency === "euro"
                          ? selectedBooking?.variantsBookings.reduce(
                              (total, item) =>
                                total +
                                getActivePrice(
                                  selectedBooking,
                                  item.variant.priceInEuro,
                                  item.variant.nightPriceInEuro,
                                ) *
                                  item.numberOfReservation,
                              0,
                            )
                          : selectedBooking?.variantsBookings.reduce(
                              (total, item) =>
                                total +
                                getActivePrice(
                                  selectedBooking,
                                  item.variant.price,
                                  item.variant.nightPrice,
                                ) *
                                  item.numberOfReservation,
                              0,
                            )) - selectedBooking?.priceToPay}{" "}
                        {selectedBooking.priceToPayCurrency === "euro"
                          ? "€"
                          : "DH"}
                        )
                      </CCol>
                    </CRow>
                  </>
                )}

              <CRow className="mb-2 align-items-center justify-content-between border-top pt-2 mt-2">
                <CCol sm="auto">
                  <strong style={{ fontSize: "14px" }}>Total:</strong>
                </CCol>
                <CCol sm="auto">
                  <strong>
                    {selectedBooking?.priceToPay}{" "}
                    {selectedBooking?.priceToPayCurrency === "euro"
                      ? "€"
                      : "DH"}
                  </strong>
                </CCol>
              </CRow>
            </div>
          ) : selectedBooking?.special ? (
            // ==========================================
            // BRANCH 2: STANDARD OFFER (NO VARIANTS)
            // ==========================================
            <div className="mt-4 bg-primary-shade p-2 rounded-sm">
              <p className="mb-2 border-bottom fw-bold">Offre réservée</p>

              <CRow
                key={selectedBooking?.special?.id || selectedBooking?.id}
                className="mb-2 align-items-center justify-content-between"
              >
                <CCol sm="auto">
                  {selectedBooking?.special?.title ||
                    selectedBooking?.event?.name ||
                    "Offre"}
                </CCol>
              </CRow>
              {selectedBooking?.appliedPromoCodes &&
                selectedBooking?.appliedPromoCodes?.length > 0 && (
                  <>
                    <CRow className=" mt-3 pt-2 mb-2 align-items-center justify-content-between border-top">
                      <CCol sm="auto">
                        <strong style={{ fontSize: "14px" }}>
                          Sous-Total:
                        </strong>
                      </CCol>
                      <CCol sm="auto">
                        {selectedBooking.priceToPayCurrency === "euro"
                          ? (selectedBooking?.special?.numberOfReservation ||
                              selectedBooking?.personsNumber) *
                            getActivePrice(
                              selectedBooking,
                              selectedBooking?.special?.priceInEuro,
                              selectedBooking?.special?.nightPriceInEuro,
                            )
                          : (selectedBooking?.special?.numberOfReservation ||
                              selectedBooking?.personsNumber) *
                            getActivePrice(
                              selectedBooking,
                              selectedBooking?.special?.price,
                              selectedBooking?.special?.nightPrice,
                            )}{" "}
                        {selectedBooking.priceToPayCurrency === "euro"
                          ? "€"
                          : "DH"}
                      </CCol>
                    </CRow>
                    <CRow className="mb-2 align-items-center justify-content-between">
                      <CCol sm="auto">
                        <strong style={{ fontSize: "14px" }}>
                          Remise (
                          {selectedBooking?.appliedPromoCodes[0].discount}%):
                        </strong>
                      </CCol>
                      <CCol sm="auto">
                        - (
                        {selectedBooking.priceToPayCurrency === "euro"
                          ? (selectedBooking?.special?.numberOfReservation ||
                              selectedBooking?.personsNumber) *
                              getActivePrice(
                                selectedBooking,
                                selectedBooking?.special?.priceInEuro,
                                selectedBooking?.special?.nightPriceInEuro,
                              ) -
                            selectedBooking?.priceToPay
                          : (selectedBooking?.special?.numberOfReservation ||
                              selectedBooking?.personsNumber) *
                              getActivePrice(
                                selectedBooking,
                                selectedBooking?.special?.price,
                                selectedBooking?.special?.nightPrice,
                              ) -
                            selectedBooking?.priceToPay}{" "}
                        {selectedBooking.priceToPayCurrency === "euro"
                          ? "€"
                          : "DH"}
                        )
                      </CCol>
                    </CRow>
                  </>
                )}

              <CRow className="mb-2 align-items-center justify-content-between border-top pt-2 mt-2">
                <CCol sm="auto">
                  <strong style={{ fontSize: "14px" }}>Total:</strong>
                </CCol>
                <CCol sm="auto">
                  <strong>
                    {selectedBooking?.priceToPay}{" "}
                    {selectedBooking?.priceToPayCurrency === "euro"
                      ? "€"
                      : "DH"}
                  </strong>
                </CCol>
              </CRow>
            </div>
          ) : null}

          {selectedBooking?.servicesBookings!?.length > 0 && (
            <div className="mt-4 bg-primary-shade p-2 rounded-sm">
              <p className="mb-2 border-bottom fw-bold">Service réservé</p>
              {selectedBooking?.servicesBookings.map((item) => (
                <CRow
                  key={item.id}
                  className="mb-2 align-items-center justify-content-between"
                >
                  <CCol sm="auto">
                    {item.personsNumber} x {item.service.label}
                  </CCol>
                  {selectedBooking.priceToPayCurrency === "euro" ? (
                    <CCol sm="auto">
                      {item.personsNumber * item.service.startingPriceEuro} €
                    </CCol>
                  ) : (
                    <CCol sm="auto">
                      {item.personsNumber * item.service.startingPrice} DH
                    </CCol>
                  )}
                </CRow>
              ))}
              {selectedBooking?.appliedPromoCodes &&
                selectedBooking?.appliedPromoCodes?.length > 0 && (
                  <>
                    <CRow className=" mt-3 pt-2 mb-2 align-items-center justify-content-between border-top">
                      <CCol sm="auto">
                        <strong style={{ fontSize: "14px" }}>
                          Sous-Total:
                        </strong>
                      </CCol>
                      {selectedBooking.priceToPayCurrency === "euro" ? (
                        <CCol sm="auto">
                          {selectedBooking?.servicesBookings?.reduce(
                            (total, service) => {
                              return (
                                total +
                                service.service.startingPriceEuro *
                                  service.personsNumber
                              );
                            },
                            0,
                          )}{" "}
                          €
                        </CCol>
                      ) : (
                        <CCol sm="auto">
                          {selectedBooking?.servicesBookings?.reduce(
                            (total, service) => {
                              return (
                                total +
                                service.service.startingPrice *
                                  service.personsNumber
                              );
                            },
                            0,
                          )}{" "}
                          DH
                        </CCol>
                      )}
                    </CRow>
                    {selectedBooking && (
                      <CRow className="mb-2 align-items-center justify-content-between">
                        <CCol sm="auto">
                          <strong style={{ fontSize: "14px" }}>
                            {" "}
                            Remise (
                            {selectedBooking?.appliedPromoCodes[0].discount}
                            %):
                          </strong>
                        </CCol>
                        {selectedBooking.priceToPayCurrency === "euro" ? (
                          <CCol sm="auto">
                            -
                            {selectedBooking?.servicesBookings.reduce(
                              (total, service) =>
                                total +
                                service.service.startingPriceEuro *
                                  service.personsNumber,
                              0,
                            ) - selectedBooking?.priceToPay}{" "}
                            €
                          </CCol>
                        ) : (
                          <CCol sm="auto">
                            -
                            {selectedBooking?.servicesBookings.reduce(
                              (total, service) =>
                                total +
                                service.service.startingPrice *
                                  service.personsNumber,
                              0,
                            ) - selectedBooking?.priceToPay}{" "}
                            DH
                          </CCol>
                        )}
                      </CRow>
                    )}
                  </>
                )}
              <CRow className="mb-2 align-items-center justify-content-between">
                <CCol sm="auto">
                  {" "}
                  <strong style={{ fontSize: "14px" }}>Total:</strong>{" "}
                </CCol>
                <CCol sm="auto">
                  <strong>
                    {selectedBooking?.priceToPay ??
                      selectedBooking?.servicesBookings?.reduce(
                        (total, service) => {
                          return (
                            total +
                            service.service.startingPrice *
                              service.personsNumber
                          );
                        },
                        0,
                      )}{" "}
                    {selectedBooking?.priceToPayCurrency === "euro"
                      ? "€"
                      : "DH"}
                  </strong>{" "}
                </CCol>
              </CRow>
            </div>
          )}

          {selectedBooking?.createdAt && (
            <div className="border-top p-1 pt-2 mt-3">
              <p className="mb-0">
                Date de création :{" "}
                {formatDateString(selectedBooking?.createdAt)}
              </p>
              <p className="mb-0">
                Heure de création :{" "}
                {format(new Date(selectedBooking?.createdAt), "HH:mm")}
              </p>
            </div>
          )}

          {selectedBooking?.date && (
            <div className="border-top p-1 pt-2 mt-3">
              <p className="mb-0">
                Date de réservation : {formatDateString(selectedBooking?.date)}
              </p>
              <p className="mb-0">
                Heure de réservation : {selectedBooking?.hour}
              </p>
            </div>
          )}

          {selectedBooking?.scanedBy && (
            <div className="border-top p-1 mt-3">
              <p className="mb-0">
                Scanné par: {selectedBooking?.scanedBy?.fullName}{" "}
              </p>
            </div>
          )}
          {selectedBooking?.scanedAt && (
            <div className="border-bottom p-1">
              <p className="mb-0">
                Scanné à:{" "}
                {format(
                  new Date(selectedBooking?.scanedAt),
                  "dd/MM/yyyy - HH:mm",
                )}
              </p>
            </div>
          )}

          {/* //TODO: should get the booking for details from the backend */}
          {selectedBooking?.bookingFor?.name && (
            <div className="border-top p-1 mt-3">
              <h6 className="mb-0">Réservé pour :</h6>
              <p className="mb-0">
                Nom de la personne: {selectedBooking?.bookingFor?.name}
              </p>
              <p className="mb-0">
                Email: {selectedBooking?.bookingFor?.email}
              </p>
              <p className="mb-0">
                Téléphone: {selectedBooking?.bookingFor?.phone}
              </p>
            </div>
          )}
        </COffcanvasBody>
      </COffcanvas>
    </div>
  );
}
